import { EuiButton, EuiButtonProps } from "@inscopix/ideas-eui";
import { useQuery } from "@tanstack/react-query";
import { fetchToolVersionDocsTemplate } from "components/ToolEditor/api/fetchToolVersionDocs";
import { Tooltip } from "components/Tooltip/Tooltip";
import { queryKeys } from "django/queryKeys";
import { useState } from "react";

interface ButtonCopyToolVersionDocsProps
  extends Omit<EuiButtonProps, "onClick"> {
  toolVersionId: number;
}

export const ButtonCopyToolVersionDocs = ({
  toolVersionId,
  ...props
}: ButtonCopyToolVersionDocsProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.toolVersionDocsTemplate(toolVersionId),
    queryFn: () => fetchToolVersionDocsTemplate(toolVersionId),
    refetchOnWindowFocus: false,
  });
  const handleCopy = () => {
    if (data && data.docs_template !== "") {
      void navigator.clipboard.writeText(data.docs_template);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000); // reset after 2 seconds
    }
  };

  const toolTipContent = (() => {
    if (isError) {
      return "Error loading documentation template";
    } else if (isLoading) {
      return "Loading documentation template...";
    } else if (data.docs_template === "") {
      return "No documentation template available to copy";
    } else {
      return "Copy documentation template to clipboard";
    }
  })();

  const buttonText = (() => {
    if (isLoading) {
      return "Loading...";
    } else if (isCopied) {
      return "Copied!";
    } else {
      return "Copy documentation template";
    }
  })();

  return (
    <Tooltip content={toolTipContent}>
      <EuiButton
        onClick={handleCopy}
        disabled={isLoading || isError || data?.docs_template === ""}
        iconType={isCopied ? "check" : "copyClipboard"}
        {...props}
      >
        {buttonText}
      </EuiButton>
    </Tooltip>
  );
};
